import React, { useMemo } from 'react';
import { Box, Typography, Stack, Link } from '@mui/material';
import ProfileServiceCard from './ProfileServiceCard';
import ProfilePageCard from './ProfilePageCard';

// Muestra ~5 tarjetas (≈216px c/u) y activa scroll cuando hay más
const scrollSx = (count, itemHeight = 216) => count > 5 ? {
  maxHeight: itemHeight * 5.4,
  overflowY: 'auto',
  pr: 1,
  '&::-webkit-scrollbar': { width: '6px' },
  '&::-webkit-scrollbar-track': { bgcolor: '#F1F5F9', borderRadius: '10px' },
  '&::-webkit-scrollbar-thumb': { bgcolor: '#CBD5E1', borderRadius: '10px' }
} : {};

const SectionHeader = ({ title, subtitle, count, onSeeMore }) => (
  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', mb: 3 }}>
    <Box>
      <Typography variant="h5" fontWeight="bold" color="primary" sx={{ mb: 0.5 }}>
        {title}
      </Typography>
      <Typography variant="body2" color="#737791">
        {subtitle}
      </Typography>
    </Box>
    {count > 3 && (
      <Link
        component="button"
        variant="body2"
        onClick={onSeeMore}
        sx={{
          color: '#0E2465',
          fontWeight: 'bold',
          textDecoration: 'none',
          '&:hover': { textDecoration: 'underline' }
        }}
      >
        Ver más ({count})
      </Link>
    )}
  </Box>
);

const ProfileServiceList = ({ profile, user }) => {
  const userName = user?.name || profile?.nombre || 'el proveedor';

  const { independentServices, independentPages, groupPages } = useMemo(() => {
    const services = profile?.servicios || [];
    const pages = profile?.paginas || [];
    
    // Services already assigned to pages
    const servicesInPages = pages.reduce((acc, p) => {
      return [...acc, ...(p.servicios || []).map(sId => sId.toString())];
    }, []);

    // 1. Independent Services (not in any page)
    const indServices = services.filter(s => !servicesInPages.includes(s._id.toString()));

    // 2. Independent Pages (1 member)
    const indPages = pages.filter(p => {
      const acceptedMembers = (p.miembros || []).filter(m => m.status === 1);
      return acceptedMembers.length === 1;
    });

    // 3. Group Pages (>1 member)
    const grPages = pages.filter(p => {
      const acceptedMembers = (p.miembros || []).filter(m => m.status === 1);
      return acceptedMembers.length > 1;
    });

    return {
      independentServices: indServices,
      independentPages: indPages,
      groupPages: grPages
    };
  }, [profile]);

  return (
    <Box sx={{ width: '100%' }}>
      {/* SECTION 1: Independent Services */}
      <Box sx={{ mb: 6 }}>
        <SectionHeader 
          title="Solo servicios" 
          subtitle={`que ${userName} brinda`}
          count={independentServices.length}
          onSeeMore={() => console.log('See more services')}
        />
        <Box sx={scrollSx(independentServices.length)}>
          <Stack spacing={2}>
            {independentServices.length > 0 ? (
              independentServices.map((service, index) => (
                <ProfileServiceCard
                  key={service._id || index}
                  service={service}
                  user={user}
                  profile={profile}
                />
              ))
            ) : (
              <Typography variant="body2" color="#737791" sx={{ fontStyle: 'italic', pb: 2 }}>
                No hay servicios independientes disponibles.
              </Typography>
            )}
          </Stack>
        </Box>
      </Box>

      {/* SECTION 2: Independent Pages */}
      <Box sx={{ mb: 6 }}>
        <SectionHeader 
          title="Páginas de Servicios Independientes" 
          subtitle={`que ${userName} brinda y administra`}
          count={independentPages.length}
          onSeeMore={() => console.log('See more independent pages')}
        />
        <Box sx={scrollSx(independentPages.length)}>
          <Stack spacing={2}>
            {independentPages.length > 0 ? (
              independentPages.map((page, index) => (
                <ProfilePageCard
                  key={page._id || index}
                  page={page}
                  user={user}
                  profile={profile}
                />
              ))
            ) : (
              <Typography variant="body2" color="#737791" sx={{ fontStyle: 'italic', pb: 2 }}>
                No hay páginas de servicios independientes disponibles.
              </Typography>
            )}
          </Stack>
        </Box>
      </Box>

      {/* SECTION 3: Group Pages */}
      <Box sx={{ mb: 6 }}>
        <SectionHeader 
          title="Páginas de Grupos y Negocios" 
          subtitle={`de los que ${userName} es parte`}
          count={groupPages.length}
          onSeeMore={() => console.log('See more group pages')}
        />
        <Box sx={scrollSx(groupPages.length)}>
          <Stack spacing={2}>
            {groupPages.length > 0 ? (
              groupPages.map((page, index) => (
                <ProfilePageCard
                  key={page._id || index}
                  page={page}
                  user={user}
                  profile={profile}
                  isGroup
                />
              ))
            ) : (
              <Typography variant="body2" color="#737791" sx={{ fontStyle: 'italic', pb: 2 }}>
                No participa actualmente en grupos o negocios.
              </Typography>
            )}
          </Stack>
        </Box>
      </Box>
    </Box>
  );
};

export default ProfileServiceList;
